import { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganization } from "@/contexts/OrganizationContext";
import { Loader2 } from "lucide-react";

// Internal employee domains - these users skip onboarding and go to backoffice
const INTERNAL_DOMAINS = ["growthleverdemo.com", "growthlever.com"];

interface ProtectedRouteProps {
  children: ReactNode;
  /**
   * Frontend-only admin check for UX purposes.
   * SECURITY NOTE: Real authorization is enforced via RLS policies in the database
   * using has_role(auth.uid(), 'admin'). This frontend check prevents unnecessary
   * API calls and provides a better user experience, but the database is the
   * source of truth for access control.
   */
  requireAdmin?: boolean;
  /**
   * Skip organization requirement check (only for onboarding page itself)
   */
  skipOrgCheck?: boolean;
}

const isInternalEmployee = (email: string | undefined): boolean => {
  if (!email) return false;
  const lower = email.toLowerCase();
  return INTERNAL_DOMAINS.some(domain => lower.endsWith(`@${domain}`));
};

const ProtectedRoute = ({ children, requireAdmin = false, skipOrgCheck = false }: ProtectedRouteProps) => {
  const { user, isAdmin, isLoading: authLoading, isProfileLoading } = useAuth();
  const { organizations, isLoading: orgLoading } = useOrganization();
  const location = useLocation();

  // Wait for all loading states including profile to prevent race conditions
  const isLoading = authLoading || orgLoading || isProfileLoading;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  const isInternal = isInternalEmployee(user.email);
  const hasOrganization = organizations.length > 0;

  // Internal employees (admins) skip onboarding - they can see all orgs
  if (isInternal) {
    // If they're on onboarding, redirect to backoffice
    if (location.pathname === "/onboarding") {
      return <Navigate to="/backoffice" replace />;
    }
    // Internal users can access admin routes
    if (requireAdmin && !isAdmin) {
      // Still allow internal employees even if role not yet set
    }
  } else {
    // Regular users MUST have an organization
    // If they don't have one and aren't on onboarding, redirect to onboarding
    if (!hasOrganization && !skipOrgCheck && location.pathname !== "/onboarding") {
      return <Navigate to="/onboarding" replace />;
    }

    // Regular users need admin check
    if (requireAdmin && !isAdmin) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
